import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface IngestDashboardData {
  filings: {
    text_status: Record<string, number>;
    pdf_status: Record<string, number>;
  };
  dockets: {
    pdf_ingest_status: Record<string, number>;
  };
  backfill_seed: Record<string, number>;
  jobs: Record<string, number>;
  recent_errors: IngestError[];
}

export interface IngestError {
  id: string;
  pipeline: string;
  docket_number: string | null;
  filing_id: string | null;
  submission_id: string | null;
  error_message: string;
  failed_at: string;
}

const API_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ingest-dashboard`;
const DEFAULT_POLL_MS = 15_000;

export function useIngestDashboard(pollIntervalMs = DEFAULT_POLL_MS) {
  const [data, setData] = useState<IngestDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.status === 403) {
        setError('Forbidden');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const json: IngestDashboardData = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(fetchData, pollIntervalMs);
  }, [fetchData, pollIntervalMs]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [autoRefresh, startPolling, stopPolling]);

  return { data, loading, error, refresh: fetchData, autoRefresh, setAutoRefresh };
}
